/**
 * @fileoverview データをフォーマットするユーティリティ関数
 */

import dateFns from '@/utilities/date'
import {
  DATE_FORMAT_NORMAL,
  DATE_FORMAT_JA,
  DATE_FORMAT_LITERAL_DAY_BEFORE_YESTERDAY,
  DATE_FORMAT_LITERAL_YESTERDAY,
  TIME_FORMAT,
} from '@/constants/dateFormat'

/**
 * 全角英数を半角に変換する
 *
 * @param value 全角英数
 * @return 半角英数
 */
export function formatToHankaku(value: string): string {
  return value.replace(/[Ａ-Ｚａ-ｚ０-９]/g, s => {
    return String.fromCharCode(s.charCodeAt(0) - 65248)
  })
}

/**
 * ひらがなをカタカナに変換する
 *
 * @description 長音符（ー）・カタカナ・英数字・漢字・記号はそのまま返す
 * @param value 変換前文字列
 * @return ひらがな部分をカタカナに変換した文字列
 */
export function formatHiraganaToKatakana(value: string): string {
  return value.replace(/[ぁ-ゖ]/g, s => {
    return String.fromCharCode(s.charCodeAt(0) + 0x60)
  })
}

/**
 * カタカナをひらがなに変換する
 *
 * @description 長音符（ー）・ひらがな・英数字・漢字・記号、および半角カタカナはそのまま返す
 * @param value 変換前文字列
 * @return カタカナ部分をひらがなに変換した文字列
 */
export function formatKatakanaToHiragana(value: string): string {
  return value.replace(/[ァ-ヶ]/g, s => {
    return String.fromCharCode(s.charCodeAt(0) - 0x60)
  })
}

/**
 * かな欄の表記ゆれを取り除く
 *
 * @description 半角カタカナと結合文字（NFD）の濁点を NFKC で合成したうえで、カタカナをひらがなへ寄せ、空白（半角・全角）を取り除く
 * @description 数字・英字・記号は変換せずそのまま残す（バリデーション側で弾く）
 * @param value 変換前文字列
 * @return 正規化した文字列
 */
export function normalizeKana(value: string): string {
  return formatKatakanaToHiragana(value.normalize('NFKC')).replace(/\s/g, '')
}

/**
 * 文字列がひらがな（長音符「ー」を含む）のみで構成されているか判定する
 *
 * @description 漢字・カタカナ・英数字・記号が 1 文字でも混じる場合、および空文字列は false
 * @param value 判定対象文字列
 */
export function isHiraganaOnly(value: string): boolean {
  return /^[ぁ-ゖー]+$/.test(value)
}

/**
 * 先頭大文字・2文字目以降小文字の形に変換する
 *
 * @param value 変換前文字列
 */
export function formatInitialLetterToUpper(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

/**
 * 文字列内に含まれるアルファベットを全て大文字に変換する
 *
 * @param value 変換前文字列
 */
export function formatToUpper(value: string): string {
  return value.toUpperCase()
}

/**
 * "true" や "false" という「boolean っぽい」文字列を string から boolean に変換する
 *
 * @param value 変換前文字列（「true」や「false」といった boolean っぽい文字列）
 */
export function formatStrToBoolean(value: string | undefined | null): boolean {
  if (value === null || value === undefined) return false

  return value.toLowerCase() === 'true'
}

/**
 * 数値の桁区切り
 *
 * @example 10000 -> 10,000
 * @param value 数字
 * @return 桁区切り整形済み数値文字列
 */
export function numberDigitsDelimiter(value: number): string {
  // value の型チェック（数値でない場合はエラー）
  if (!Number.isInteger(value)) console.error('invalid value exception')

  return value.toLocaleString()
}

/**
 * 指定した桁数を超えないように調整する
 *
 * @param value
 * @param digit 桁数上限
 * @example sliceNumber(123456789, 8) -> 12345678
 */
export function sliceNumber(value: number, digit: number): number {
  if (!Number.isInteger(value)) return value

  return parseInt(value.toString().slice(0, digit), 10)
}

/**
 * 価格フォーマット
 * 引数で送られた数値を、3 桁区切り且つ指定の価格単位を添えて返却
 * 引数で送られた値が数字でない場合は、そのまま返却
 *
 * @param value 価格の数字、または価格文言
 * @param suffix 価格単位 [default: 円]
 * @return 整形済み価格表記
 */
export function priceFormat(value: string | number, suffix: string = '円'): string | number {
  let _value: number
  let parsedValue: number

  if (Number.isInteger(value)) {
    _value = Number(value)
  } else {
    parsedValue = parseInt(value.toString(), 10)

    // value の型チェック（数値でない場合はそのまま返却）
    if (isNaN(parsedValue)) return value

    _value = parsedValue
  }

  return numberDigitsDelimiter(_value) + suffix
}

/**
 * 数値のゼロ埋め
 * @example 9 -> 09
 * @param value 基準となる数値
 * @param length フォーマット桁数
 * @return ゼロ埋め整形済み文字列
 */
export function zeroPadding(value: number, length: number = 2): string {
  return (Array(length).join('0') + value).slice(-length)
}

export function localeDate(value: string | Date, format: string = DATE_FORMAT_NORMAL): string | Date {
  if (!dateFns.isValid(new Date(value))) return value

  const _value = typeof value === 'string' ? new Date(value) : value
  return dateFns.format(_value, format)
}

export function localeDateJa(value: string | Date, format: string = DATE_FORMAT_JA): string | Date {
  if (!dateFns.isValid(new Date(value))) return value

  const _value = typeof value === 'string' ? new Date(value) : value
  return dateFns.format(_value, format, { locale: dateFns.localeJa })
}

/**
 * 日付の日本語表記特殊フォーマットの、フォーマット文字列を返却する（整形済み日付文字列を返却するわけではない）
 * @description コンポーネントにフォーマットのみを渡したいとき等に使用
 * @param value フォーマット対象の日付
 * @param format 3 日以上前の場合の日付フォーマット（デフォルト: yyyy年MM月dd日）
 * @returns 本日の場合 -> HH:mm
 *          昨日の場合 -> 「昨日」（date-fns のエスケープ記法）
 *          一昨日の場合 -> 「一昨日」（date-fns のエスケープ記法）
 *          3 日以上前・明日以降の場合 -> 引数の format
 */
export function dateFormatJa2(value: string | Date, format: string = DATE_FORMAT_JA): string {
  if (!dateFns.isValid(new Date(value))) return format

  const _value = typeof value === 'string' ? new Date(value) : value

  // 一昨日
  const DAY_BEFORE_YESTERDAY = dateFns.subDays(new Date(), 2)

  if (dateFns.isToday(_value)) return TIME_FORMAT

  if (dateFns.isYesterday(_value)) return DATE_FORMAT_LITERAL_YESTERDAY

  // 未来日付を「一昨日」と扱わないため、下限との比較ではなく同一日で判定する
  if (dateFns.isSameDay(_value, DAY_BEFORE_YESTERDAY)) return DATE_FORMAT_LITERAL_DAY_BEFORE_YESTERDAY

  return format
}

/**
 * htmlタグ付きの文字列を素の文字列に変換する
 *
 * @param {String} value htmlタグ付きの文字列
 * @returns {String} 素の文字列
 */
export function removeHTMLTag(value: string): string {
  return value.replace(/<("[^"]*"|'[^']*'|[^'">])*>/g, '')
}

/**
 * 改行コードを html の br タグに変換する
 * @param value 対象文字列
 * @returns
 */
export function toBrTagFromNewLineCharacter(value: string): string {
  return value.replace(/\n/g, '<br>')
}

/**
 * 行頭・空白文字を削除する
 * テンプレートリテラルを使って複数行に渡る文字列の空白削除等に使います。
 *
 * @param value 変換対象文字列
 * @returns 変換後文字列
 */
export function removeSpaceInHeadOfLine(value: string): string {
  return value.replace(/(?<=^|\r|\r?\n)\s+/g, '')
}

/**
 * 文字列をセグメント単位に分解する
 * @description 文字数を正確にカウントする際等に利用する
 * @see https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Intl/Segmenter
 * @see https://qiita.com/bon127/items/491b25e90208188dafbd
 *
 * @param value 対象文字列
 * @returns { Segments } 文字列のセグメントインスタンス
 */
export function reduceStringToSegments(value: string) {
  const segmenter = new Intl.Segmenter('ja', { granularity: 'grapheme' })
  return segmenter.segment(value)
}

/**
 * 指定の最大文字数を超過した場合、文字列を切り詰め、省略分は「...」として処理する
 *
 * @param value 対象文字列
 * @param { Number } maxLangth 最大文字数
 * @returns
 */
export function truncateString(value: string, maxLangth: number): string {
  const segments = reduceStringToSegments(value)
  return [...segments].length <= maxLangth
    ? value
    : `${value.slice(0, (maxLangth - 3))}...`
}

/**
 * 「秒」の時間を「HH:mm:ss」表記に変換
 * @param value 「秒」単位の時間
 * @description value: 6234 -> 01:43:54
 */
export function formatToHms(value: number): string {
  if (!Number.isFinite(value) || value < 0) return value.toString()
  let hms = ''
  const h = Math.floor(value / 3600)
  const m = Math.floor(value % 3600 / 60)
  const s = Math.round(value % 60)

  if (h !== 0) {
    hms = h.toString() + ':' + zeroPadding(m) + ':' + zeroPadding(s)
  } else if (m !== 0) {
    hms = m.toString() + ':' + zeroPadding(s)
  } else {
    hms = s.toString()
  }
  return hms
}
